import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import AdminService from "../../../api/services/adminService";
import { imageUrl } from "../../../api/apiConfig/apiConfig";
import "../Home/Homepage.css";
import "./TradingCommissionPage.css";

/** Build absolute URL for coin.icon paths like icons/... */
const resolveCoinIconUrl = (path) => {
  if (!path || typeof path !== "string") return "";
  const p = path.trim();
  if (/^https?:\/\//i.test(p)) return p;
  const base = String(imageUrl || "").replace(/\/$/, "");
  return `${base}/${p.replace(/^\//, "")}`;
};

const formatDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const formatNum = (v, opts = {}) => {
  if (v == null || v === "") return "—";
  const n = typeof v === "number" ? v : Number(v);
  if (Number.isNaN(n)) return String(v);
  return n.toLocaleString("en-IN", opts);
};

const formatPct = (v) => {
  if (v == null || v === "") return "—";
  const s = String(v).trim();
  if (s.endsWith("%")) return s;
  return `${s}%`;
};

const FEE_TYPE_LABELS = {
  maker_fee: "MAKER FEE",
  taker_fee: "TAKER FEE",
  admin_commission: "ADMIN COMMISSION",
};

/** Maps API fee_type to display label */
const formatFeeTypeLabel = (raw) => {
  const k = String(raw ?? "").trim().toLowerCase();
  if (FEE_TYPE_LABELS[k]) return FEE_TYPE_LABELS[k];
  if (!raw) return "—";
  return String(raw).replace(/_/g, " ").toUpperCase();
};

/** CSS modifier: maker_fee → maker, taker_fee → taker, admin_commission → admin */
const feeTypeClassSuffix = (raw) => {
  const k = String(raw ?? "").trim().toLowerCase();
  if (k === "maker_fee") return "maker";
  if (k === "taker_fee") return "taker";
  if (k === "admin_commission") return "admin";
  return k.replace(/[^a-z0-9_-]/gi, "-").replace(/_+/g, "_") || "unknown";
};

const TradingCommissionPage = () => {
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState("");
  const [search, setSearch] = useState("");
  const [viewOrder, setViewOrder] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalCount: 0,
    totalPages: 1,
  });

  const [activeTab, setActiveTab] = useState("commission");
  /* P2P Fee tab (commented out)
  const [p2pList, setP2pList] = useState([]);
  const [p2pLoading, setP2pLoading] = useState(false);
  const [p2pError, setP2pError] = useState("");
  const [p2pSearch, setP2pSearch] = useState("");
  const [p2pAggregateFee, setP2pAggregateFee] = useState(null);
  const [p2pTotalUsers, setP2pTotalUsers] = useState(null);
  const [p2pPagination, setP2pPagination] = useState({
    page: 1,
    limit: 10,
    totalCount: 0,
    totalPages: 1,
  });
  */
  const [futuresList, setFuturesList] = useState([]);
  const [futuresLoading, setFuturesLoading] = useState(false);
  const [futuresError, setFuturesError] = useState("");
  const [futuresSearch, setFuturesSearch] = useState("");
  const [futuresTotalFee, setFuturesTotalFee] = useState(null);
  const [futuresPagination, setFuturesPagination] = useState({
    page: 1,
    limit: 10,
    totalCount: 0,
    totalPages: 1,
  });

  const fetchCommissions = useCallback(async () => {
    setLoading(true);
    setFetchError("");
    try {
      const res = await AdminService.getAdminTradingCommission({
        page: pagination.page,
        limit: pagination.limit,
      });
      if (!res?.success) {
        setCommissions([]);
        setFetchError(res?.message || "Failed to load commission history");
        return;
      }
      const raw = res.data;
      const items = Array.isArray(raw) ? raw : raw && typeof raw === "object" && Array.isArray(raw.items) ? raw.items : [];
      setCommissions(items);
      const totalCount = Number(res.totalCount ?? res.total ?? items.length) || 0;
      const totalPages = Math.max(1, Number(res.totalPages) || 1);
      const currentPage = Number(res.currentPage ?? pagination.page) || 1;
      setPagination((p) => ({
        ...p,
        totalCount,
        totalPages,
        page: currentPage,
      }));
    } catch {
      setCommissions([]);
      setFetchError("Failed to load commission history");
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit]);

  /* P2P Fee tab (commented out)
  const fetchP2pFees = useCallback(async () => {
    setP2pLoading(true);
    setP2pError("");
    try {
      const res = await AdminService.getAdminP2pFees({
        page: p2pPagination.page,
        limit: p2pPagination.limit,
      });
      if (!res?.success) {
        setP2pList([]);
        setP2pAggregateFee(null);
        setP2pTotalUsers(null);
        setP2pError(res?.message || "Failed to load P2P fees");
        return;
      }
      const payload = res.data;
      const list =
        payload && typeof payload === "object" && Array.isArray(payload.list)
          ? payload.list
          : [];
      setP2pList(list);
      const agg = payload?.total_fee;
      setP2pAggregateFee(agg != null && agg !== "" && !Number.isNaN(Number(agg)) ? Number(agg) : null);
      const usersAgg = payload?.total_users;
      setP2pTotalUsers(
        usersAgg != null && usersAgg !== "" && !Number.isNaN(Number(usersAgg)) ? Number(usersAgg) : null
      );
      const totalCount = Number(res.totalCount ?? list.length) || 0;
      const limit = p2pPagination.limit;
      const totalPages = Math.max(
        1,
        Number(res.totalPages) || (limit > 0 ? Math.ceil(totalCount / limit) : 1)
      );
      const currentPage = Number(res.currentPage ?? p2pPagination.page) || 1;
      setP2pPagination((p) => ({
        ...p,
        totalCount,
        totalPages,
        page: currentPage,
      }));
    } catch {
      setP2pList([]);
      setP2pAggregateFee(null);
      setP2pTotalUsers(null);
      setP2pError("Failed to load P2P fees");
    } finally {
      setP2pLoading(false);
    }
  }, [p2pPagination.page, p2pPagination.limit]);
  */

  const fetchFuturesCommission = useCallback(async () => {
    setFuturesLoading(true);
    setFuturesError("");
    try {
      const res = await AdminService.getAdminFuturesCommission({
        page: futuresPagination.page,
        limit: futuresPagination.limit,
      });
      if (!res?.success) {
        setFuturesList([]);
        setFuturesTotalFee(null);
        setFuturesError(res?.message || "Failed to load futures commission");
        return;
      }
      const list = Array.isArray(res.data) ? res.data : [];
      setFuturesList(list);
      const totalFee = Number(res.totalFee);
      setFuturesTotalFee(Number.isNaN(totalFee) ? null : totalFee);
      const totalCount = Number(res.totalCount ?? list.length) || 0;
      const totalPages = Math.max(
        1,
        Number(res.totalPages) || (futuresPagination.limit > 0 ? Math.ceil(totalCount / futuresPagination.limit) : 1)
      );
      const currentPage = Number(res.currentPage ?? futuresPagination.page) || 1;
      setFuturesPagination((p) => ({
        ...p,
        totalCount,
        totalPages,
        page: currentPage,
      }));
    } catch {
      setFuturesList([]);
      setFuturesTotalFee(null);
      setFuturesError("Failed to load futures commission");
    } finally {
      setFuturesLoading(false);
    }
  }, [futuresPagination.page, futuresPagination.limit]);

  useEffect(() => {
    if (activeTab !== "commission") return;
    fetchCommissions();
  }, [activeTab, fetchCommissions]);

  /* P2P Fee tab (commented out)
  useEffect(() => {
    if (activeTab !== "p2p") return;
    fetchP2pFees();
  }, [activeTab, fetchP2pFees]);
  */

  useEffect(() => {
    if (activeTab !== "futures") return;
    fetchFuturesCommission();
  }, [activeTab, fetchFuturesCommission]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return commissions;
    return commissions.filter((row) => {
      const uuid = String(row.user?.uuid ?? "").toLowerCase();
      const orderId = String(row.order_details?.orderId ?? row.fromOrder ?? "").toLowerCase();
      const coin = String(row.coin?.short_name ?? "").toLowerCase();
      const feeType = String(row.fee_type ?? "").toLowerCase();
      const feeTypeLabel = formatFeeTypeLabel(row.fee_type).toLowerCase();
      return (
        uuid.includes(q) ||
        orderId.includes(q) ||
        coin.includes(q) ||
        feeType.includes(q) ||
        feeTypeLabel.includes(q) ||
        String(row._id ?? "").toLowerCase().includes(q)
      );
    });
  }, [commissions, search]);

  /* P2P Fee tab (commented out)
  const filteredP2p = useMemo(() => {
    const q = p2pSearch.trim().toLowerCase();
    if (!q) return p2pList;
    return p2pList.filter((row) => {
      const uuid = String(row.uuid ?? "").toLowerCase();
      const name = `${row.firstName ?? ""} ${row.lastName ?? ""}`.trim().toLowerCase();
      const email = String(row.emailId ?? row.email ?? "").toLowerCase();
      const mobile = String(row.mobileNumber ?? "").toLowerCase();
      return (
        uuid.includes(q) ||
        name.includes(q) ||
        email.includes(q) ||
        mobile.includes(q) ||
        String(row.user_id ?? "").toLowerCase().includes(q)
      );
    });
  }, [p2pList, p2pSearch]);
  */

  const filteredFutures = useMemo(() => {
    const q = futuresSearch.trim().toLowerCase();
    if (!q) return futuresList;
    return futuresList.filter((row) => {
      const symbol = String(row.symbol ?? "").toLowerCase();
      const side = String(row.side ?? "").toLowerCase();
      const role = String(row.role ?? "").toLowerCase();
      const userName = String(row.user?.name ?? "").toLowerCase();
      const userEmail = String(row.user?.email ?? "").toLowerCase();
      const pairName = String(row.pair?.name ?? row.pair?.short_name ?? "").toLowerCase();
      const status = String(row.order?.status ?? "").toLowerCase();
      return (
        symbol.includes(q) ||
        side.includes(q) ||
        role.includes(q) ||
        userName.includes(q) ||
        userEmail.includes(q) ||
        pairName.includes(q) ||
        status.includes(q)
      );
    });
  }, [futuresList, futuresSearch]);

  const goToPage = (next) => {
    if (next < 1 || next > pagination.totalPages) return;
    setPagination((p) => ({ ...p, page: next }));
  };

  /* P2P Fee tab (commented out)
  const goP2pPage = (next) => {
    if (next < 1 || next > p2pPagination.totalPages) return;
    setP2pPagination((p) => ({ ...p, page: next }));
  };
  */

  const goFuturesPage = (next) => {
    if (next < 1 || next > futuresPagination.totalPages) return;
    setFuturesPagination((p) => ({ ...p, page: next }));
  };

  return (
    <main className="dashboard-main dashboard-subpage">
      <section className="dashboard-hero">
        <div className="dashboard-hero__text">
          <p className="dashboard-hero__breadcrumb">
            <Link to="/dashboard/homepage">Dashboard</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>Trading Commission</span>
          </p>
          <h1 className="dashboard-hero__title">Trading Commission</h1>
          <p className="dashboard-hero__subtitle">
            Configure trading commission tiers and view commission history
          </p>
        </div>
        <div className="dashboard-hero__icon">
          <i className="fas fa-chart-pie" />
        </div>
      </section>

      <div className="dashboard-body">
        <div className="commission-tabs" role="tablist" aria-label="Trading commission views">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "commission"}
            className={`commission-tab ${activeTab === "commission" ? "commission-tab--active" : ""}`}
            onClick={() => setActiveTab("commission")}
          >
            <i className="fas fa-receipt" aria-hidden />
            Spot Trading Commission
          </button>
          {/* <button
            type="button"
            role="tab"
            aria-selected={activeTab === "p2p"}
            className={`commission-tab ${activeTab === "p2p" ? "commission-tab--active" : ""}`}
            onClick={() => setActiveTab("p2p")}
          >
            <i className="fas fa-handshake" aria-hidden />
            P2P Fee
          </button> */}
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "futures"}
            className={`commission-tab ${activeTab === "futures" ? "commission-tab--active" : ""}`}
            onClick={() => setActiveTab("futures")}
          >
            <i className="fas fa-chart-line" aria-hidden />
            Futures Commission
          </button>
        </div>

        {activeTab === "commission" && (
        <section className="commission-section">
          <div className="commission-section__header">
            <div className="commission-section__title-wrap">
              <span className="commission-section__icon">
                <i className="fas fa-percentage" />
              </span>
              <h2 className="commission-section__title">Trading Commission History</h2>
            </div>
            <div className="commission-section__stats">
              <span className="commission-stat">
                <strong>{pagination.totalCount}</strong> Records
              </span>
            </div>
          </div>
          {fetchError && (
            <p className="commission-empty commission-empty--error" role="alert">{fetchError}</p>
          )}
          <div className="commission-search-wrap">
            <i className="fas fa-search" />
            <input
              type="text"
              placeholder="Filter by user UUID, order ID, currency, fee type…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="commission-search-input"
            />
          </div>
          <div className="commission-table-wrap">
            <table className="commission-table">
              <thead>
                <tr>
                  <th>User UUID</th>
                  <th>Created</th>
                  <th>Currency</th>
                  <th>Order ID</th>
                  <th>Amount</th>
                  <th>%</th>
                  <th>Fee</th>
                  <th>Fee type</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="9" className="commission-table__loading">
                      <i className="fas fa-spinner fa-spin" /> Loading…
                    </td>
                  </tr>
                ) : (
                  filtered.map((row) => {
                    const uuid = row.user?.uuid;
                    const orderId = row.order_details?.orderId ?? row.fromOrder ?? "—";
                    const feeTypeSuffix = feeTypeClassSuffix(row.fee_type);
                    const coinIconSrc = resolveCoinIconUrl(row.coin?.icon);
                    return (
                      <tr key={row._id}>
                        <td><span className="commission-mono">{uuid ?? "—"}</span></td>
                        <td>{formatDate(row.createdAt)}</td>
                        <td>
                          <span className="commission-currency">
                            {coinIconSrc ? (
                              <img
                                src={coinIconSrc}
                                alt={row.coin?.short_name ? `${row.coin.short_name} icon` : ""}
                                className="commission-currency__icon"
                                width={28}
                                height={28}
                                loading="lazy"
                                decoding="async"
                              />
                            ) : (
                              <span className="commission-currency__icon commission-currency__icon--placeholder" aria-hidden>
                                <i className="fas fa-coins" />
                              </span>
                            )}
                            <strong className="commission-currency__name">{row.coin?.short_name ?? "—"}</strong>
                          </span>
                        </td>
                        <td><span className="commission-mono">{orderId}</span></td>
                        <td>{formatNum(row.amount, { maximumFractionDigits: 8 })}</td>
                        <td>{formatPct(row.percentage)}</td>
                        <td>{formatNum(row.fee, { minimumFractionDigits: 2, maximumFractionDigits: 8 })}</td>
                        <td>
                          <span className={`commission-fee-type commission-fee-type--${feeTypeSuffix}`}>
                            {formatFeeTypeLabel(row.fee_type)}
                          </span>
                        </td>
                        <td>
                          {row.order_details && (
                            <button
                              type="button"
                              className="commission-btn commission-btn--view"
                              onClick={() => setViewOrder(row)}
                            >
                              <i className="fas fa-eye" /> View Order
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          {!loading && filtered.length === 0 && !fetchError && (
            <p className="commission-empty">
              {commissions.length === 0 && !search.trim()
                ? "No commission records yet."
                : "No commission records match your filter."}
            </p>
          )}
          {!loading && pagination.totalPages > 1 && (
            <div className="commission-pagination">
              <button
                type="button"
                className="commission-page-btn"
                onClick={() => goToPage(pagination.page - 1)}
                disabled={pagination.page <= 1}
              >
                Prev
              </button>
              <span>
                Page {pagination.page} of {pagination.totalPages}
              </span>
              <button
                type="button"
                className="commission-page-btn"
                onClick={() => goToPage(pagination.page + 1)}
                disabled={pagination.page >= pagination.totalPages}
              >
                Next
              </button>
            </div>
          )}
        </section>
        )}

        {/* {activeTab === "p2p" && (
        <section className="commission-section">
          <div className="commission-section__header">
            <div className="commission-section__title-wrap">
              <span className="commission-section__icon">
                <i className="fas fa-handshake" />
              </span>
              <h2 className="commission-section__title">P2P Fee</h2>
            </div>
            <div className="commission-section__stats">
              {p2pTotalUsers != null && (
                <span className="commission-stat">
                  <strong>{p2pTotalUsers}</strong> Users
                </span>
              )}
              <span className="commission-stat">
                <strong>{p2pPagination.totalCount}</strong> Records
              </span>
              {p2pAggregateFee != null && (
                <span className="commission-stat">
                  <strong>{formatNum(p2pAggregateFee, { maximumFractionDigits: 8 })}</strong> Total fee
                </span>
              )}
            </div>
          </div>
          {p2pError && (
            <p className="commission-empty commission-empty--error" role="alert">{p2pError}</p>
          )}
          <div className="commission-search-wrap">
            <i className="fas fa-search" />
            <input
              type="text"
              placeholder="Filter by UUID, name, email, mobile…"
              value={p2pSearch}
              onChange={(e) => setP2pSearch(e.target.value)}
              className="commission-search-input"
            />
          </div>
          <div className="commission-table-wrap">
            <table className="commission-table commission-table--p2p">
              <thead>
                <tr>
                  <th>UUID</th>
                  <th>User</th>
                  <th>Email</th>
                  <th>Created</th>
                  <th>Total orders</th>
                  <th>Total crypto</th>
                  <th>Total fiat</th>
                  <th>Total fee</th>
                </tr>
              </thead>
              <tbody>
                {p2pLoading ? (
                  <tr>
                    <td colSpan="8" className="commission-table__loading">
                      <i className="fas fa-spinner fa-spin" /> Loading…
                    </td>
                  </tr>
                ) : (
                  filteredP2p.map((row, idx) => {
                    const fullName = [row.firstName, row.lastName].filter(Boolean).join(" ").trim() || "—";
                    const rowKey = `${row.user_id ?? "u"}-${row.createdAt ?? idx}-${idx}`;
                    return (
                      <tr key={rowKey}>
                        <td><span className="commission-mono">{row.uuid ?? "—"}</span></td>
                        <td>{fullName}</td>
                        <td>{row.emailId ?? row.email ?? "—"}</td>
                        <td>{formatDate(row.createdAt)}</td>
                        <td>{formatNum(row.total_orders, { maximumFractionDigits: 0 })}</td>
                        <td>{formatNum(row.total_crypto, { maximumFractionDigits: 8 })}</td>
                        <td>{formatNum(row.total_fiat, { maximumFractionDigits: 2 })}</td>
                        <td>{formatNum(row.total_fee, { minimumFractionDigits: 2, maximumFractionDigits: 8 })}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          {!p2pLoading && filteredP2p.length === 0 && !p2pError && (
            <p className="commission-empty">
              {p2pList.length === 0 && !p2pSearch.trim()
                ? "No P2P fee records yet."
                : "No records match your filter."}
            </p>
          )}
          {!p2pLoading && p2pPagination.totalPages > 1 && (
            <div className="commission-pagination">
              <button
                type="button"
                className="commission-page-btn"
                onClick={() => goP2pPage(p2pPagination.page - 1)}
                disabled={p2pPagination.page <= 1}
              >
                Prev
              </button>
              <span>
                Page {p2pPagination.page} of {p2pPagination.totalPages}
              </span>
              <button
                type="button"
                className="commission-page-btn"
                onClick={() => goP2pPage(p2pPagination.page + 1)}
                disabled={p2pPagination.page >= p2pPagination.totalPages}
              >
                Next
              </button>
            </div>
          )}
        </section>
        )} */}

        {activeTab === "futures" && (
        <section className="commission-section">
          <div className="commission-section__header">
            <div className="commission-section__title-wrap">
              <span className="commission-section__icon">
                <i className="fas fa-chart-line" />
              </span>
              <h2 className="commission-section__title">Futures Commission</h2>
            </div>
            <div className="commission-section__stats">
              <span className="commission-stat">
                <strong>{futuresPagination.totalCount}</strong> Records
              </span>
              {futuresTotalFee != null && (
                <span className="commission-stat">
                  <strong>{formatNum(futuresTotalFee, { maximumFractionDigits: 8 })}</strong> Total fee
                </span>
              )}
            </div>
          </div>
          {futuresError && (
            <p className="commission-empty commission-empty--error" role="alert">{futuresError}</p>
          )}
          <div className="commission-search-wrap">
            <i className="fas fa-search" />
            <input
              type="text"
              placeholder="Filter by symbol, side, role, user, pair, status..."
              value={futuresSearch}
              onChange={(e) => setFuturesSearch(e.target.value)}
              className="commission-search-input"
            />
          </div>
          <div className="commission-table-wrap">
            <table className="commission-table commission-table--futures">
              <thead>
                <tr>
                  <th>Created</th>
                  <th>User</th>
                  <th>Symbol</th>
                  <th>Side</th>
                  <th>Role</th>
                  <th>Quantity</th>
                  <th>Price</th>
                  <th>Fee</th>
                  <th>Leverage</th>
                  <th>Order Type</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {futuresLoading ? (
                  <tr>
                    <td colSpan="11" className="commission-table__loading">
                      <i className="fas fa-spinner fa-spin" /> Loading…
                    </td>
                  </tr>
                ) : (
                  filteredFutures.map((row, idx) => {
                    const rowKey = row._id || `${row.symbol ?? "symbol"}-${row.createdAt ?? idx}-${idx}`;
                    const side = String(row.side ?? "—").toUpperCase();
                    const role = String(row.role ?? "—").toUpperCase();
                    return (
                      <tr key={rowKey}>
                        <td>{formatDate(row.createdAt)}</td>
                        <td>
                          <div className="commission-user-cell">
                            <strong>{row.user?.uuid ?? "—"}</strong>
                          </div>
                        </td>
                        <td><span className="commission-mono">{row.symbol ?? "—"}</span></td>
                        <td><span className={`commission-side commission-side--${side.toLowerCase() === "long" ? "buy" : side.toLowerCase() === "short" ? "sell" : "neutral"}`}>{side}</span></td>
                        <td><span className="commission-chain">{role}</span></td>
                        <td>{formatNum(row.quantity, { maximumFractionDigits: 8 })}</td>
                        <td>{formatNum(row.price, { maximumFractionDigits: 8 })}</td>
                        <td>{formatNum(row.fee, { minimumFractionDigits: 2, maximumFractionDigits: 8 })}</td>
                        <td>{formatNum(row.order?.leverage, { maximumFractionDigits: 0 })}x</td>
                        <td>{row.order?.type ?? "—"}</td>
                        <td>{row.order?.status ?? "—"}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          {!futuresLoading && filteredFutures.length === 0 && !futuresError && (
            <p className="commission-empty">
              {futuresList.length === 0 && !futuresSearch.trim()
                ? "No futures commission records yet."
                : "No records match your filter."}
            </p>
          )}
          {!futuresLoading && futuresPagination.totalPages > 1 && (
            <div className="commission-pagination">
              <button
                type="button"
                className="commission-page-btn"
                onClick={() => goFuturesPage(futuresPagination.page - 1)}
                disabled={futuresPagination.page <= 1}
              >
                Prev
              </button>
              <span>
                Page {futuresPagination.page} of {futuresPagination.totalPages}
              </span>
              <button
                type="button"
                className="commission-page-btn"
                onClick={() => goFuturesPage(futuresPagination.page + 1)}
                disabled={futuresPagination.page >= futuresPagination.totalPages}
              >
                Next
              </button>
            </div>
          )}
        </section>
        )}
      </div>

      {/* Order details modal */}
      {viewOrder?.order_details && (() => {
        const od = viewOrder.order_details;
        const sideRaw = (od.side || "").toLowerCase();
        const sideClass = sideRaw === "buy" || sideRaw === "sell" ? sideRaw : "neutral";
        const priceStr =
          typeof od.price === "number"
            ? od.price.toLocaleString("en-IN", { maximumFractionDigits: 8 })
            : od.price;
        const qtyStr =
          typeof od.quantity === "number"
            ? od.quantity.toLocaleString("en-IN", { maximumFractionDigits: 8 })
            : od.quantity;
        const statusRaw = (od.status || "").toString();
        const statusClass = statusRaw.toLowerCase().replace(/\s+/g, "-");
        return (
          <div className="commission-modal-overlay" onClick={() => setViewOrder(null)} role="presentation">
            <div
              className="commission-modal commission-modal--premium"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="commission-order-modal-title"
            >
              <header className="commission-modal__hero">
                <button
                  type="button"
                  className="commission-modal__close"
                  onClick={() => setViewOrder(null)}
                  aria-label="Close"
                >
                  <i className="fas fa-times" />
                </button>
                <p className="commission-modal__eyebrow">
                  <i className="fas fa-file-invoice" aria-hidden />
                  Order Details
                </p>
                <h2 id="commission-order-modal-title" className="commission-modal__title">
                  <span className="commission-modal__id">{od.orderId ?? viewOrder.fromOrder}</span>
                </h2>
                {od.createdAt && (
                  <p className="commission-modal__meta">
                    <i className="far fa-clock" aria-hidden />
                    {formatDate(od.createdAt)}
                  </p>
                )}
              </header>

              <div className="commission-modal__body commission-modal__body--premium">
                <section className="commission-modal__section" aria-labelledby="commission-section-pair">
                  <h3 id="commission-section-pair" className="commission-modal__section-title">
                    <span className="commission-modal__section-icon" aria-hidden>
                      <i className="fas fa-exchange-alt" />
                    </span>
                    Trading pair
                  </h3>
                  <div className="commission-modal__pair-grid">
                    <div className="commission-modal__pair-card">
                      <span className="commission-modal__pair-label">Ask (base)</span>
                      <span className="commission-modal__pair-value">{od.ask_currency ?? "—"}</span>
                    </div>
                    <div className="commission-modal__pair-card">
                      <span className="commission-modal__pair-label">Pay (quote)</span>
                      <span className="commission-modal__pair-value">{od.pay_currency ?? "—"}</span>
                    </div>
                  </div>
                </section>

                <section className="commission-modal__section" aria-labelledby="commission-section-exec">
                  <h3 id="commission-section-exec" className="commission-modal__section-title">
                    <span className="commission-modal__section-icon" aria-hidden>
                      <i className="fas fa-bolt" />
                    </span>
                    Execution
                  </h3>
                  <div className="commission-modal__exec-grid">
                    <div className="commission-modal__stat">
                      <span className="commission-modal__stat-label">Side</span>
                      <span className={`commission-side commission-side--lg commission-side--${sideClass}`}>
                        {od.side != null ? String(od.side) : "—"}
                      </span>
                    </div>
                    <div className="commission-modal__stat">
                      <span className="commission-modal__stat-label">Order type</span>
                      <span className="commission-modal__stat-value">{od.order_type ?? "—"}</span>
                    </div>
                    <div className="commission-modal__stat commission-modal__stat--span2">
                      <span className="commission-modal__stat-label">Price</span>
                      <span className="commission-modal__stat-value commission-modal__stat-value--num">{priceStr}</span>
                    </div>
                    <div className="commission-modal__stat commission-modal__stat--span2">
                      <span className="commission-modal__stat-label">Quantity</span>
                      <span className="commission-modal__stat-value commission-modal__stat-value--num">{qtyStr}</span>
                    </div>
                  </div>
                </section>

                <section className="commission-modal__section commission-modal__section--status" aria-labelledby="commission-section-status">
                  <h3 id="commission-section-status" className="commission-modal__section-title">
                    <span className="commission-modal__section-icon" aria-hidden>
                      <i className="fas fa-flag-checkered" />
                    </span>
                    Status
                  </h3>
                  <div className="commission-modal__status-banner">
                    <span className={`commission-order-status commission-order-status--${statusClass}`}>
                      <i className="fas fa-check-circle" aria-hidden />
                      {statusRaw || "—"}
                    </span>
                  </div>
                </section>
              </div>

              <footer className="commission-modal__footer">
                <button type="button" className="commission-modal__btn-done" onClick={() => setViewOrder(null)}>
                  Done
                </button>
              </footer>
            </div>
          </div>
        );
      })()}
    </main>
  );
};

export default TradingCommissionPage;
